import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Button from "../../../components/button/Button";
import Input from "../../../components/input/Input";
import Select from "react-select";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import styles from "./BillingInvoiceDetailScreen.module.scss";
import classNames from "classnames";
import moment from "moment";
import {
  fetchBillingInvoice,
  fetchBillingInvoiceResetState,
  updateBillingInvoice,
  updateBillingInvoiceResetState,
  updateBillingInvoiceReference,
  updateBillingInvoiceReferenceResetState,
  sendBillingInvoice,
  sendBillingInvoiceResetState,
  cancelBillingInvoice,
  cancelBillingInvoiceResetState,
  togglePaidBillingInvoice,
  togglePaidBillingInvoiceResetState,
  fetchBillingInvoiceItems,
  createBillingInvoiceItem,
  createBillingInvoiceItemResetState,
  updateBillingInvoiceItem,
  updateBillingInvoiceItemResetState,
  deleteBillingInvoiceItem,
  deleteBillingInvoiceItemResetState,
  fetchBillingInvoicePayments,
  createBillingInvoicePayment,
  createBillingInvoicePaymentResetState,
  updateBillingInvoicePayment,
  updateBillingInvoicePaymentResetState,
  deleteBillingInvoicePayment,
  deleteBillingInvoicePaymentResetState,
} from "../../../store/actions/admin/billingInvoices";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Poslato" },
  { value: "paid", label: "Plaćeno" },
  { value: "overdue", label: "Zakasnelo" },
  { value: "cancelled", label: "Otkazano" },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "bank_transfer", label: "Bankovni transfer" },
  { value: "cash", label: "Gotovina" },
  { value: "card", label: "Kartica" },
  { value: "other", label: "Ostalo" },
];

const BillingInvoiceDetailScreen = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const invoice = useSelector((state) => state.billingInvoices.singleInvoice);
  const items = useSelector((state) => state.billingInvoices.items);
  const payments = useSelector((state) => state.billingInvoices.payments);
  const updateInvoice = useSelector((state) => state.billingInvoices.updateInvoice);
  const updateReference = useSelector((state) => state.billingInvoices.updateReference);
  const sendInvoice = useSelector((state) => state.billingInvoices.sendInvoice);
  const cancelInvoice = useSelector((state) => state.billingInvoices.cancelInvoice);
  const togglePaid = useSelector((state) => state.billingInvoices.togglePaid);
  const createItem = useSelector((state) => state.billingInvoices.createItem);
  const updateItem = useSelector((state) => state.billingInvoices.updateItem);
  const deleteItem = useSelector((state) => state.billingInvoices.deleteItem);
  const createPayment = useSelector((state) => state.billingInvoices.createPayment);
  const updatePayment = useSelector((state) => state.billingInvoices.updatePayment);
  const deletePayment = useSelector((state) => state.billingInvoices.deletePayment);

  const [editMode, setEditMode] = useState(false);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState(null);
  const [reference, setReference] = useState("");
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Item modals
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemName, setItemName] = useState("");
  const [itemType, setItemType] = useState("manual");
  const [itemDescription, setItemDescription] = useState("");
  const [itemQuantity, setItemQuantity] = useState("");
  const [itemUnitPrice, setItemUnitPrice] = useState("");
  const [itemDiscountPercent, setItemDiscountPercent] = useState("");
  const [itemDiscountAmount, setItemDiscountAmount] = useState("");
  const [itemOrder, setItemOrder] = useState("0");

  // Payment modals
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  // Delete modals
  const [deleteItemModalOpen, setDeleteItemModalOpen] = useState(false);
  const [deletePaymentModalOpen, setDeletePaymentModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  useEffect(() => {
    // Redirect if id is "bulk-create" - this shouldn't be treated as an invoice ID
    if (id === "bulk-create") {
      history.replace("/billing-invoices/bulk-create");
      return;
    }
    
    if (id) {
      loadInvoice();
      loadItems();
      loadPayments();
    }
  }, [id]);

  useEffect(() => {
    if (invoice.data) {
      setNotes(invoice.data.notes || "");
      setStatus(STATUS_OPTIONS.find((s) => s.value === invoice.data.status) || null);
      setReference(invoice.data.reference || "");
    }
  }, [invoice.data]);

  useEffect(() => {
    if (updateInvoice.success) {
      toast.success("Faktura uspešno ažurirana");
      dispatch(updateBillingInvoiceResetState());
      loadInvoice();
      setEditMode(false);
    }
  }, [updateInvoice.success, dispatch]);

  useEffect(() => {
    if (sendInvoice.success) {
      toast.success("Faktura uspešno poslata");
      dispatch(sendBillingInvoiceResetState());
      loadInvoice();
      setSendModalOpen(false);
    }
  }, [sendInvoice.success, dispatch]);

  useEffect(() => {
    if (cancelInvoice.success) {
      toast.success("Faktura uspešno otkazana");
      dispatch(cancelBillingInvoiceResetState());
      loadInvoice();
      setCancelModalOpen(false);
    }
  }, [cancelInvoice.success, dispatch]);

  useEffect(() => {
    if (togglePaid.success) {
      toast.success(invoice.data?.isPaid ? "Faktura označena kao plaćena" : "Faktura označena kao neplaćena");
      dispatch(togglePaidBillingInvoiceResetState());
      loadInvoice();
    }
  }, [togglePaid.success, dispatch, invoice.data?.isPaid]);

  useEffect(() => {
    if (togglePaid.error) {
      toast.error("Greška pri promeni statusa plaćanja");
      dispatch(togglePaidBillingInvoiceResetState());
    }
  }, [togglePaid.error, dispatch]);

  useEffect(() => {
    if (updateReference.success) {
      toast.success("Referenca uspešno ažurirana");
      dispatch(updateBillingInvoiceReferenceResetState());
      loadInvoice();
    }
  }, [updateReference.success, dispatch]);

  useEffect(() => {
    if (updateReference.error) {
      toast.error("Greška pri ažuriranju reference");
      dispatch(updateBillingInvoiceReferenceResetState());
    }
  }, [updateReference.error, dispatch]);

  useEffect(() => {
    if (createItem.success) {
      toast.success("Stavka uspešno dodata");
      dispatch(createBillingInvoiceItemResetState());
      loadItems();
      loadInvoice();
      closeItemModal();
    }
  }, [createItem.success, dispatch]);

  useEffect(() => {
    if (updateItem.success) {
      toast.success("Stavka uspešno ažurirana");
      dispatch(updateBillingInvoiceItemResetState());
      loadItems();
      loadInvoice();
      closeItemModal();
    }
  }, [updateItem.success, dispatch]);

  useEffect(() => {
    if (deleteItem.success) {
      toast.success("Stavka uspešno obrisana");
      dispatch(deleteBillingInvoiceItemResetState());
      loadItems();
      loadInvoice();
      setDeleteItemModalOpen(false);
      setItemToDelete(null);
    }
  }, [deleteItem.success, dispatch]);

  useEffect(() => {
    if (createPayment.success) {
      toast.success("Plaćanje uspešno dodato");
      dispatch(createBillingInvoicePaymentResetState());
      loadPayments();
      loadInvoice();
      closePaymentModal();
    }
  }, [createPayment.success, dispatch]);

  useEffect(() => {
    if (updatePayment.success) {
      toast.success("Plaćanje uspešno ažurirano");
      dispatch(updateBillingInvoicePaymentResetState());
      loadPayments();
      loadInvoice();
      closePaymentModal();
    }
  }, [updatePayment.success, dispatch]);

  useEffect(() => {
    if (deletePayment.success) {
      toast.success("Plaćanje uspešno obrisano");
      dispatch(deleteBillingInvoicePaymentResetState());
      loadPayments();
      loadInvoice();
      setDeletePaymentModalOpen(false);
      setPaymentToDelete(null);
    }
  }, [deletePayment.success, dispatch]);

  const loadInvoice = () => {
    dispatch(fetchBillingInvoice(id));
  };

  const loadItems = () => {
    dispatch(fetchBillingInvoiceItems(id));
  };

  const loadPayments = () => {
    dispatch(fetchBillingInvoicePayments(id));
  };

  const handleUpdateInvoice = () => {
    const updateData = {};
    if (notes !== invoice.data.notes) updateData.notes = notes;
    if (status && status.value !== invoice.data.status) updateData.status = status.value;
    
    if (Object.keys(updateData).length === 0) {
      toast.info("Nema izmena za čuvanje");
      return;
    }

    dispatch(updateBillingInvoice(id, updateData));
  };

  const handleUpdateReference = () => {
    if (reference === invoice.data.reference) {
      toast.info("Nema izmena za čuvanje");
      return;
    }
    dispatch(updateBillingInvoiceReference(id, reference));
  };

  const handleSendInvoice = () => {
    dispatch(sendBillingInvoice(id));
  };

  const handleCancelInvoice = () => {
    dispatch(cancelBillingInvoice(id));
  };

  const handleTogglePaid = () => {
    dispatch(togglePaidBillingInvoice(id));
  };

  const openItemModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemName(item.name);
      setItemType(item.type || "manual");
      setItemDescription(item.description || "");
      setItemQuantity(item.quantity.toString());
      setItemUnitPrice(item.unitPrice.toString());
      setItemDiscountPercent(item.discountPercent ? item.discountPercent.toString() : "");
      setItemDiscountAmount(item.discountAmount ? item.discountAmount.toString() : "");
      setItemOrder(item.order ? item.order.toString() : "0");
    } else {
      setEditingItem(null);
      setItemName("");
      setItemType("manual");
      setItemDescription("");
      setItemQuantity("");
      setItemUnitPrice("");
      setItemDiscountPercent("");
      setItemDiscountAmount("");
      setItemOrder("0");
    }
    setItemModalOpen(true);
  };

  const closeItemModal = () => {
    setItemModalOpen(false);
    setEditingItem(null);
    setItemName("");
    setItemType("manual");
    setItemDescription("");
    setItemQuantity("");
    setItemUnitPrice("");
    setItemDiscountPercent("");
    setItemDiscountAmount("");
    setItemOrder("0");
  };

  const handleSaveItem = () => {
    if (!itemName || !itemQuantity || !itemUnitPrice) {
      toast.error("Molimo popunite sva obavezna polja");
      return;
    }

    const itemData = {
      name: itemName,
      type: itemType,
      description: itemDescription || null,
      quantity: parseFloat(itemQuantity),
      unitPrice: parseFloat(itemUnitPrice),
      discountPercent: itemDiscountPercent ? parseFloat(itemDiscountPercent) : null,
      discountAmount: itemDiscountAmount ? parseFloat(itemDiscountAmount) : 0,
      order: parseInt(itemOrder) || 0,
    };

    if (editingItem) {
      dispatch(updateBillingInvoiceItem(id, editingItem.id, itemData));
    } else {
      dispatch(createBillingInvoiceItem(id, itemData));
    }
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setDeleteItemModalOpen(true);
  };

  const confirmDeleteItem = () => {
    if (itemToDelete) {
      dispatch(deleteBillingInvoiceItem(id, itemToDelete.id));
    }
  };

  const openPaymentModal = (payment = null) => {
    if (payment) {
      setEditingPayment(payment);
      setPaymentAmount(payment.amount.toString());
      setPaymentDate(moment(payment.paymentDate).format("YYYY-MM-DD"));
      setPaymentMethod(PAYMENT_METHOD_OPTIONS.find((m) => m.value === payment.paymentMethod) || null);
      setPaymentReference(payment.reference || "");
      setPaymentNotes(payment.notes || "");
    } else {
      setEditingPayment(null);
      setPaymentAmount("");
      setPaymentDate(moment().format("YYYY-MM-DD"));
      setPaymentMethod(null);
      setPaymentReference("");
      setPaymentNotes("");
    }
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setEditingPayment(null);
    setPaymentAmount("");
    setPaymentDate(moment().format("YYYY-MM-DD"));
    setPaymentMethod(null);
    setPaymentReference("");
    setPaymentNotes("");
  };

  const handleSavePayment = () => {
    if (!paymentAmount || !paymentDate || !paymentMethod) {
      toast.error("Molimo popunite sva obavezna polja");
      return;
    }

    const paymentData = {
      amount: parseFloat(paymentAmount),
      paymentDate: moment(paymentDate).startOf("day").toISOString(),
      paymentMethod: paymentMethod.value,
      reference: paymentReference || null,
      notes: paymentNotes || null,
    };

    if (editingPayment) {
      dispatch(updateBillingInvoicePayment(id, editingPayment.id, paymentData));
    } else {
      dispatch(createBillingInvoicePayment(id, paymentData));
    }
  };

  const handleDeletePayment = (payment) => {
    setPaymentToDelete(payment);
    setDeletePaymentModalOpen(true);
  };

  const confirmDeletePayment = () => {
    if (paymentToDelete) {
      dispatch(deleteBillingInvoicePayment(id, paymentToDelete.id));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "draft":
        return styles.draft;
      case "sent":
        return styles.sent;
      case "paid":
        return styles.paid;
      case "overdue":
        return styles.overdue;
      case "cancelled":
        return styles.cancelled;
      default:
        return "";
    }
  };

  const getStatusLabel = (status) => {
    const option = STATUS_OPTIONS.find((s) => s.value === status);
    return option ? option.label : status;
  };

  if (invoice.attempt) {
    return (
      <DashboardLayout>
        <div className={styles.container}>
          <p>Učitavanje...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (invoice.error || !invoice.data) {
    return (
      <DashboardLayout>
        <div className={styles.container}>
          <p>Greška pri učitavanju fakture</p>
          <Button name="Nazad" onClick={() => history.goBack()} />
        </div>
      </DashboardLayout>
    );
  }

  const invoiceData = invoice.data;
  const itemsData = items.data || [];
  const paymentsData = payments.data?.payments || [];
  const totalPaid = payments.data?.totalPaid || 0;
  const remainingAmount = invoiceData.total - totalPaid;

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Faktura #{invoiceData.invoiceNumber}</h1>
            <span className={classNames(styles.statusBadge, getStatusBadgeClass(invoiceData.status))}>
              {getStatusLabel(invoiceData.status)}
            </span>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.backButton} onClick={() => history.goBack()}>
              ← Nazad
            </button>
          </div>
        </div>

        {/* Invoice Info Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Informacije o fakturi</h2>
            {!editMode ? (
              <Button name="Izmeni" onClick={() => setEditMode(true)} />
            ) : (
              <div className={styles.editActions}>
                <Button
                  name="Sačuvaj"
                  onClick={handleUpdateInvoice}
                  disabled={updateInvoice.attempt}
                  showLoader={updateInvoice.attempt}
                />
                <Button name="Otkaži" onClick={() => setEditMode(false)} />
              </div>
            )}
          </div>

          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Broj fakture:</label>
              <p>{invoiceData.invoiceNumber}</p>
            </div>
            <div className={styles.infoItem}>
              <label>Kompanija:</label>
              <p>{invoiceData.company?.name || "-"}</p>
            </div>
            <div className={styles.infoItem}>
              <label>Fakturiska kompanija:</label>
              <p>{invoiceData.billingCompany?.name || "-"}</p>
            </div>
            <div className={styles.infoItem}>
              <label>Šema fakture:</label>
              <p>{invoiceData.invoiceSchema?.name || "-"}</p>
            </div>
            <div className={styles.infoItem}>
              <label>Period:</label>
              <p>
                {moment(invoiceData.billingPeriodStart).format("DD.MM.YYYY")} -{" "}
                {moment(invoiceData.billingPeriodEnd).format("DD.MM.YYYY")}
              </p>
            </div>
            <div className={styles.infoItem}>
              <label>Tip perioda:</label>
              <p>
                {invoiceData.billingPeriodType === "monthly"
                  ? "Mesečno"
                  : invoiceData.billingPeriodType === "quarterly"
                  ? "Kvartalno"
                  : invoiceData.billingPeriodType === "semi-annual"
                  ? "Polugodišnje"
                  : "Godišnje"}
              </p>
            </div>
            <div className={styles.infoItem}>
              <label>Datum izdavanja:</label>
              <p>{moment(invoiceData.issueDate).format("DD.MM.YYYY")}</p>
            </div>
            <div className={styles.infoItem}>
              <label>Rok plaćanja:</label>
              <p>{moment(invoiceData.dueDate).format("DD.MM.YYYY")}</p>
            </div>
            <div className={styles.infoItem}>
              <label>Status:</label>
              <span className={classNames(styles.statusBadge, styles[invoiceData.status])}>
                {invoiceData.status === "draft"
                  ? "Draft"
                  : invoiceData.status === "sent"
                  ? "Poslato"
                  : invoiceData.status === "paid"
                  ? "Plaćeno"
                  : invoiceData.status === "overdue"
                  ? "Zakasnelo"
                  : "Otkazano"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <label>Plaćeno:</label>
              <span className={classNames(styles.statusBadge, invoiceData.isPaid ? styles.paid : styles.unpaid)}>
                {invoiceData.isPaid ? "Da" : "Ne"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <label>Referenca:</label>
              {editMode ? (
                <div className={styles.referenceEditGroup}>
                  <input
                    type="text"
                    className={styles.referenceInput}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    maxLength={255}
                    placeholder="Unesite referencu"
                  />
                  <Button
                    name="Sačuvaj"
                    onClick={handleUpdateReference}
                    disabled={updateReference.attempt}
                    showLoader={updateReference.attempt}
                  />
                </div>
              ) : (
                <p>{invoiceData.reference || "-"}</p>
              )}
            </div>
            {editMode && (
              <>
                <div className={styles.infoItem}>
                  <label>Status:</label>
                  <Select
                    options={STATUS_OPTIONS}
                    value={status}
                    onChange={setStatus}
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "100%",
                      }),
                      control: (provided) => ({
                        ...provided,
                        minHeight: "40px",
                      }),
                    }}
                  />
                </div>
                <div className={styles.infoItemFull}>
                  <label>Napomene:</label>
                  <textarea
                    className={styles.textarea}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                  />
                </div>
              </>
            )}
            {!editMode && invoiceData.notes && (
              <div className={styles.infoItemFull}>
                <label>Napomene:</label>
                <p>{invoiceData.notes}</p>
              </div>
            )}
          </div>

          <div className={styles.totals}>
            <div className={styles.totalRow}>
              <span>Međuzbir:</span>
              <span>{typeof invoiceData.subtotal === "number" ? invoiceData.subtotal.toFixed(2) : (parseFloat(invoiceData.subtotal) || 0).toFixed(2)} RSD</span>
            </div>
            <div className={styles.totalRow}>
              <span>Ukupan popust:</span>
              <span>{typeof invoiceData.totalDiscount === "number" ? invoiceData.totalDiscount.toFixed(2) : (parseFloat(invoiceData.totalDiscount) || 0).toFixed(2)} RSD</span>
            </div>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Ukupno:</span>
              <span className={styles.totalAmount}>{typeof invoiceData.total === "number" ? invoiceData.total.toFixed(2) : (parseFloat(invoiceData.total) || 0).toFixed(2)} RSD</span>
            </div>
            <div className={styles.totalRow}>
              <span>Plaćeno:</span>
              <span>{typeof totalPaid === "number" ? totalPaid.toFixed(2) : (parseFloat(totalPaid) || 0).toFixed(2)} RSD</span>
            </div>
            <div className={styles.totalRow}>
              <span>Preostalo:</span>
              <span className={remainingAmount > 0 ? styles.remaining : styles.paid}>
                {typeof remainingAmount === "number" ? remainingAmount.toFixed(2) : (parseFloat(remainingAmount) || 0).toFixed(2)} RSD
              </span>
            </div>
          </div>

          {invoiceData.status !== "cancelled" && invoiceData.status !== "paid" && (
            <div className={styles.actionButtons}>
              {invoiceData.status === "draft" && (
                <Button
                  name="Pošalji fakturu"
                  onClick={() => setSendModalOpen(true)}
                  disabled={sendInvoice.attempt}
                />
              )}
              <Button
                name="Otkaži fakturu"
                onClick={() => setCancelModalOpen(true)}
                disabled={cancelInvoice.attempt}
              />
            </div>
          )}
          <div className={styles.actionButtons}>
            <Button
              name={invoiceData.isPaid ? "Označi kao neplaćeno" : "Označi kao plaćeno"}
              onClick={handleTogglePaid}
              disabled={togglePaid.attempt}
              showLoader={togglePaid.attempt}
            />
          </div>
        </div>

        {/* Billing Company Information Section */}
        {invoiceData.billingCompany && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Na koga ide faktura</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Naziv kompanije:</label>
                <p>{invoiceData.billingCompany.name || "-"}</p>
              </div>
              {invoiceData.billingCompany.taxId && (
                <div className={styles.infoItem}>
                  <label>PIB:</label>
                  <p>{invoiceData.billingCompany.taxId}</p>
                </div>
              )}
              {invoiceData.billingCompany.address && (
                <div className={styles.infoItem}>
                  <label>Adresa:</label>
                  <p>{invoiceData.billingCompany.address}</p>
                </div>
              )}
              {(invoiceData.billingCompany.contactFirstName || invoiceData.billingCompany.contactLastName) && (
                <div className={styles.infoItem}>
                  <label>Kontakt osoba:</label>
                  <p>
                    {[invoiceData.billingCompany.contactFirstName, invoiceData.billingCompany.contactLastName]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </p>
                </div>
              )}
              {invoiceData.billingCompany.contactEmail && (
                <div className={styles.infoItem}>
                  <label>Email:</label>
                  <p>
                    <a href={`mailto:${invoiceData.billingCompany.contactEmail}`} className={styles.link}>
                      {invoiceData.billingCompany.contactEmail}
                    </a>
                  </p>
                </div>
              )}
              {invoiceData.billingCompany.contactPhone && (
                <div className={styles.infoItem}>
                  <label>Telefon:</label>
                  <p>
                    <a href={`tel:${invoiceData.billingCompany.contactPhone}`} className={styles.link}>
                      {invoiceData.billingCompany.contactPhone}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invoice Items Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Stavke fakture</h2>
            {invoiceData.status === "draft" && (
              <Button name="+ Dodaj stavku" onClick={() => openItemModal()} />
            )}
          </div>

          {itemsData.length === 0 ? (
            <p className={styles.emptyMessage}>Nema stavki</p>
          ) : (
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.tableCell}>Naziv</div>
                <div className={styles.tableCell}>Tip</div>
                <div className={styles.tableCell}>Količina</div>
                <div className={styles.tableCell}>Cena</div>
                <div className={styles.tableCell}>Popust</div>
                <div className={styles.tableCell}>Ukupno</div>
                {invoiceData.status === "draft" && <div className={styles.tableCell}>Akcije</div>}
              </div>
              {itemsData.map((item) => (
                <div key={item.id} className={styles.tableRow}>
                  <div className={styles.tableCell}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.description && <p className={styles.description}>{item.description}</p>}
                    </div>
                  </div>
                  <div className={styles.tableCell}>{item.type || "manual"}</div>
                  <div className={styles.tableCell}>{item.quantity}</div>
                  <div className={styles.tableCell}>{typeof item.unitPrice === "number" ? item.unitPrice.toFixed(2) : (parseFloat(item.unitPrice) || 0).toFixed(2)} RSD</div>
                  <div className={styles.tableCell}>
                    {item.discountAmount > 0 && `-${typeof item.discountAmount === "number" ? item.discountAmount.toFixed(2) : (parseFloat(item.discountAmount) || 0).toFixed(2)} RSD`}
                    {item.discountPercent > 0 && ` (${item.discountPercent}%)`}
                  </div>
                  <div className={styles.tableCell}>{typeof item.lineTotal === "number" ? item.lineTotal.toFixed(2) : (parseFloat(item.lineTotal) || 0).toFixed(2)} RSD</div>
                  {invoiceData.status === "draft" && (
                    <div className={styles.tableCell}>
                      <button className={styles.editButton} onClick={() => openItemModal(item)}>
                        Izmeni
                      </button>
                      <button className={styles.deleteButton} onClick={() => handleDeleteItem(item)}>
                        Obriši
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invoice Payments Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Plaćanja</h2>
            {invoiceData.status !== "cancelled" && (
              <Button name="+ Dodaj plaćanje" onClick={() => openPaymentModal()} />
            )}
          </div>

          {paymentsData.length === 0 ? (
            <p className={styles.emptyMessage}>Nema plaćanja</p>
          ) : (
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.tableCell}>Datum</div>
                <div className={styles.tableCell}>Iznos</div>
                <div className={styles.tableCell}>Način plaćanja</div>
                <div className={styles.tableCell}>Referenca</div>
                <div className={styles.tableCell}>Napomene</div>
                <div className={styles.tableCell}>Akcije</div>
              </div>
              {paymentsData.map((payment) => (
                <div key={payment.id} className={styles.tableRow}>
                  <div className={styles.tableCell}>
                    {moment(payment.paymentDate).format("DD.MM.YYYY")}
                  </div>
                  <div className={styles.tableCell}>{typeof payment.amount === "number" ? payment.amount.toFixed(2) : (parseFloat(payment.amount) || 0).toFixed(2)} RSD</div>
                  <div className={styles.tableCell}>
                    {PAYMENT_METHOD_OPTIONS.find((m) => m.value === payment.paymentMethod)?.label ||
                      payment.paymentMethod}
                  </div>
                  <div className={styles.tableCell}>{payment.reference || "-"}</div>
                  <div className={styles.tableCell}>{payment.notes || "-"}</div>
                  <div className={styles.tableCell}>
                    <button className={styles.editButton} onClick={() => openPaymentModal(payment)}>
                      Izmeni
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeletePayment(payment)}
                    >
                      Obriši
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AreYouSureModal
        modalOpen={sendModalOpen}
        closeModal={() => setSendModalOpen(false)}
        onConfirm={handleSendInvoice}
      />

      <AreYouSureModal
        modalOpen={cancelModalOpen}
        closeModal={() => setCancelModalOpen(false)}
        onConfirm={handleCancelInvoice}
      />

      {/* Item Modal */}
      <BaseModal modalOpen={itemModalOpen} onClose={closeItemModal}>
        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>
            {editingItem ? "Izmeni stavku" : "Dodaj stavku"}
          </h2>
          <div className={styles.modalForm}>
            <div className={styles.formGroup}>
              <label>Naziv *</label>
              <Input value={itemName} onChange={setItemName} placeholder="Naziv stavke" />
            </div>
            <div className={styles.formGroup}>
              <label>Tip</label>
              <Input value={itemType} onChange={setItemType} placeholder="Tip stavke" />
            </div>
            <div className={styles.formGroup}>
              <label>Opis</label>
              <textarea
                className={styles.textarea}
                value={itemDescription}
                onChange={(e) => setItemDescription(e.target.value)}
                rows={3}
                placeholder="Opis stavke"
              />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Količina *</label>
                <Input
                  type="number"
                  value={itemQuantity}
                  onChange={setItemQuantity}
                  placeholder="0"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Cena po jedinici *</label>
                <Input
                  type="number"
                  value={itemUnitPrice}
                  onChange={setItemUnitPrice}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Popust (%)</label>
                <Input
                  type="number"
                  value={itemDiscountPercent}
                  onChange={setItemDiscountPercent}
                  placeholder="0"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Iznos popusta</label>
                <Input
                  type="number"
                  value={itemDiscountAmount}
                  onChange={setItemDiscountAmount}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Redosled</label>
              <Input type="number" value={itemOrder} onChange={setItemOrder} placeholder="0" />
            </div>
            <div className={styles.modalActions}>
              <Button
                name={createItem.attempt || updateItem.attempt ? "Čuvanje..." : "Sačuvaj"}
                onClick={handleSaveItem}
                disabled={createItem.attempt || updateItem.attempt}
                showLoader={createItem.attempt || updateItem.attempt}
              />
              <Button name="Otkaži" onClick={closeItemModal} />
            </div>
          </div>
        </div>
      </BaseModal>

      {/* Payment Modal */}
      <BaseModal modalOpen={paymentModalOpen} onClose={closePaymentModal}>
        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>
            {editingPayment ? "Izmeni plaćanje" : "Dodaj plaćanje"}
          </h2>
          <div className={styles.modalForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Iznos *</label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={setPaymentAmount}
                  placeholder="0.00"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Datum *</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Način plaćanja *</label>
              <Select
                options={PAYMENT_METHOD_OPTIONS}
                value={paymentMethod}
                onChange={setPaymentMethod}
                placeholder="Izaberite način plaćanja"
                styles={{
                  container: (provided) => ({
                    ...provided,
                    width: "100%",
                  }),
                  control: (provided) => ({
                    ...provided,
                    minHeight: "50px",
                  }),
                }}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Referenca</label>
              <Input
                value={paymentReference}
                onChange={setPaymentReference}
                placeholder="Referenca plaćanja"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Napomene</label>
              <textarea
                className={styles.textarea}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                rows={3}
                placeholder="Napomene o plaćanju"
              />
            </div>
            <div className={styles.modalActions}>
              <Button
                name={createPayment.attempt || updatePayment.attempt ? "Čuvanje..." : "Sačuvaj"}
                onClick={handleSavePayment}
                disabled={createPayment.attempt || updatePayment.attempt}
                showLoader={createPayment.attempt || updatePayment.attempt}
              />
              <Button name="Otkaži" onClick={closePaymentModal} />
            </div>
          </div>
        </div>
      </BaseModal>

      {/* Delete Item Modal */}
      <AreYouSureModal
        modalOpen={deleteItemModalOpen}
        closeModal={() => {
          setDeleteItemModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDeleteItem}
      />

      {/* Delete Payment Modal */}
      <AreYouSureModal
        modalOpen={deletePaymentModalOpen}
        closeModal={() => {
          setDeletePaymentModalOpen(false);
          setPaymentToDelete(null);
        }}
        onConfirm={confirmDeletePayment}
      />
    </DashboardLayout>
  );
};

export default BillingInvoiceDetailScreen;

